/* ============================================
   usePortalCliente — empresa activa del portal.

   La empresa activa la manda el JWT, no el front. Antes se guardaba aparte en
   localStorage y se podía cambiar con un selector propio: eso abría la puerta a
   que la mitad de la pantalla mirara una empresa y la otra mitad otra, porque
   los endpoints del portal ignoran `?cliente_id=` para un no-admin y usan
   siempre el token (ver portal.routes.ts). Ahora hay una sola fuente: el token.
   Para cambiar de empresa se re-emite el JWT (AccountSwitcher → switchAccount).

   Las afiliaciones (`contacto_empresa`) siguen acá porque aportan dos cosas que
   el token no trae: los roles del contacto en cada empresa y la lista completa
   de empresas para el switcher.
   ============================================ */

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import type { RolContacto } from '@/lib/types'

export interface Afiliacion {
  cliente_id: number
  empresa_nombre: string
  roles: RolContacto[]
}

interface PortalClienteCtx {
  activeClienteId: number | undefined
  afiliaciones: Afiliacion[]
  loading: boolean
}

const Ctx = createContext<PortalClienteCtx>({
  activeClienteId: undefined,
  afiliaciones: [],
  loading: true,
})

export function PortalClienteProvider({ children }: { children: ReactNode }) {
  const { clienteId, contactoId } = useAuth()
  const [afiliaciones, setAfiliaciones] = useState<Afiliacion[]>([])
  const [loading, setLoading] = useState(true)

  // La empresa activa ES la del token. Sin indirección ni estado propio.
  const activeClienteId = clienteId

  useEffect(() => {
    // Cliente sin contacto (ej. compra de hosting directa): no hay afiliaciones
    // que resolver, la empresa activa es la del token igual.
    if (!contactoId) { setAfiliaciones([]); setLoading(false); return }

    ;(async () => {
      // El endpoint ya filtra por contacto_id, acota por rol y trae el nombre
      // de la empresa via join, asi que no hace falta un segundo viaje a
      // /clientes (que ademas, para el rol cliente, responde []).
      const { data: junctions } = await supabase
        .from('contacto_empresa')
        .select('cliente_id, roles, empresa_nombre')
        .eq('contacto_id', contactoId)
        .order('id') as unknown as {
          data: { cliente_id: number; roles: RolContacto[]; empresa_nombre: string | null }[] | null
        }

      setAfiliaciones((junctions ?? []).map(j => ({
        cliente_id: j.cliente_id,
        empresa_nombre: j.empresa_nombre || `Empresa #${j.cliente_id}`,
        roles: j.roles,
      })))
      setLoading(false)
    })()
  }, [contactoId])

  return (
    <Ctx.Provider value={{ activeClienteId, afiliaciones, loading }}>
      {children}
    </Ctx.Provider>
  )
}

export function usePortalCliente() {
  return useContext(Ctx)
}
